import React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { Paths } from 'routes';
import { useTranslate } from 'services';
import { TRANSLATIONS_VALUES_KEYS } from 'redux/internationalization/internationalization';
import { useTheme, useCurrentUser } from 'hooks';
import { selectCustomerConfigurations, selectSelectedLanguage } from 'redux/root.selectors';
import { useSelector } from 'react-redux';
import { ICustomerConfigurations } from 'redux/configuration/configuration';

const {
  terms_of_service: {
    gdpr_agreement_confirmation_document_link_text,
    gdpr_agreement_confirmation_text,
    customer_privacy_policy_text,
    customer_privacy_policy_link_text,
  },
} = TRANSLATIONS_VALUES_KEYS;

const Container = styled.div`
  font-weight: bold;
  font-size: 0.75rem;
  margin-bottom: 0.75rem;
`;

const GdprPrivacyPolicyPdf = styled.span<{ color?: string }>`
  background-color: transparent;
  border: 0;
  color: ${({ color }) => color};
  text-decoration: underline;
  font-weight: bold;
`;

const TextWrapper = styled.div`
  text-align: center;
`;

type GdprPrivacyPolicyProps = {}

const GdprPrivacyPolicy : React.FC<GdprPrivacyPolicyProps> = () => {
  const history = useHistory();
  const { translateToString } = useTranslate();
  const { styles: { colorHeading } } = useTheme();

  const customerConfiguration: ICustomerConfigurations = useSelector(selectCustomerConfigurations);
  const selectedLanguage = useSelector(selectSelectedLanguage);
  const currentUser = useCurrentUser();

  const buildTextWithLink = (
    text: string,
    linkText: string,
    linkPlaceholder: string,
    onClick: () => void,
    key: string,
    variables?: Record<string, string>
  ): React.ReactNode[] => {
    const resolved = variables
      ? Object.entries(variables).reduce((acc, [k, v]) => acc.replace(`\${${k}}`, v), text)
      : text;
    const [before = '', after = ''] = resolved.split(linkPlaceholder);
    const hasSplit = resolved.includes(linkPlaceholder);
    const link = (
      <GdprPrivacyPolicyPdf key={key} data-testid="gdprPrivacyPolicyBtn" onClick={onClick} color={colorHeading}>
        {linkText}
      </GdprPrivacyPolicyPdf>
    );
    return hasSplit ? [before, link, after] : [`${resolved}\u00A0`, link];
  };

  const privacyPolicySettings = customerConfiguration?.privacyPolicySettings;
  const customerPrivacyPolicy = privacyPolicySettings?.privacyPolicies?.find((policy) => policy.languageId === selectedLanguage.id);

  const content: React.ReactNode[] = [
    ...buildTextWithLink(
      translateToString(gdpr_agreement_confirmation_text),
      translateToString(gdpr_agreement_confirmation_document_link_text),
      '${gdpr_agreement_confirmation_document_link_text}',
      () => history.push(Paths.gdprPrivacyPolicyPdf),
      'gdpr-link'
    ),
  ];

  if (privacyPolicySettings?.enabled && customerPrivacyPolicy) {
    content.push(
      ...buildTextWithLink(
        translateToString(customer_privacy_policy_text),
        translateToString(customer_privacy_policy_link_text),
        '${customer_privacy_policy_link_text}',
        () => history.push(Paths.customerPrivacyPolicyPdf.replace(':fileName', customerPrivacyPolicy.fileName)),
        'customer-policy-link',
        { customerName: currentUser?.customerName ?? '' }
      )
    );
  }

  return (
    <Container>
      <TextWrapper>
        { content }
      </TextWrapper>
    </Container>
  );
};

export { GdprPrivacyPolicy };
